import type { Settings } from '../types';
import { checkServerUrl, normalizeBaseUrl } from './connection.ts';

const STORAGE_KEY = 'collect-web-settings';

/**
 * `serverUrl` values:
 *   SAME_ORIGIN ('/') — Qodoonect's server, reached on this site's own origin:
 *                       /api/collect/* is proxied to the HIVE backend by the
 *                       Vercel rewrite (vercel.json) in production and by the
 *                       Vite proxy (vite.config.ts) locally. normalizeBaseUrl('/') === ''.
 *   'https://…'       — a self-hosted HIVE server (Settings → Advanced).
 *   ''                — nothing chosen yet: signed out (hasCredentials is false).
 *
 * Why '/' and not '': the pre-paint script in index.html (sha256 pinned in the CSP)
 * treats a saved empty serverUrl as signed out, and a missing one as "the hosted
 * default" except on localhost. '/' is signed in for it on every host, so the script
 * and the app agree (tests/landing-theme.test.mjs runs both).
 */
export const SAME_ORIGIN = '/';

// The backend address earlier builds saved as their default. Saved settings that
// still point there move to SAME_ORIGIN (same backend, through the site's own proxy).
// Only compared, never shown or requested.
const LEGACY_DEFAULT_SERVER = 'https://synapse-api-production.up.railway.app';

// localhost: no saved choice = signed out (the inline script says so too). Signing
// in saves SAME_ORIGIN, which the Vite dev proxy serves.
const DEV_HOST = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/.test(window.location.hostname);

// In dev, pre-fill API key from .env.local (VITE_DEFAULT_API_KEY) so npm run dev
// works with one click of "Sign in". Never read in prod builds (env var unset).
const DEV_API_KEY = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_DEFAULT_API_KEY) || '';

export function defaultSettings(devHost: boolean = DEV_HOST, devApiKey: string = DEV_API_KEY): Settings {
  return { serverUrl: devHost ? '' : SAME_ORIGIN, apiKey: devHost ? devApiKey : '' };
}

/** Is this saved serverUrl the old built-in default? (scheme, case and trailing slash ignored) */
export function isLegacyDefaultServer(serverUrl: string): boolean {
  return normalizeBaseUrl(serverUrl).toLowerCase() === LEGACY_DEFAULT_SERVER;
}

/**
 * Saved JSON → settings. Pure (tests/landing-theme.test.mjs checks it against the
 * pre-paint script). `migrated`: the saved value was the old default and should be
 * rewritten as SAME_ORIGIN.
 */
export function parseStoredSettings(
  raw: string | null,
  devHost: boolean = DEV_HOST,
  devApiKey: string = DEV_API_KEY,
): { settings: Settings; migrated: boolean } {
  const defaults = defaultSettings(devHost, devApiKey);
  let parsed: unknown = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    parsed = null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { settings: defaults, migrated: false };
  const o = parsed as Record<string, unknown>;
  const apiKey = 'apiKey' in o ? (typeof o.apiKey === 'string' ? o.apiKey : '') : defaults.apiKey;
  if (!('serverUrl' in o)) return { settings: { serverUrl: defaults.serverUrl, apiKey }, migrated: false };
  const saved = typeof o.serverUrl === 'string' ? o.serverUrl : '';
  if (saved && isLegacyDefaultServer(saved)) return { settings: { serverUrl: SAME_ORIGIN, apiKey }, migrated: true };
  return { settings: { serverUrl: saved, apiKey }, migrated: false };
}

export function loadSettings(): Settings {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return defaultSettings();
  }
  const { settings, migrated } = parseStoredSettings(raw);
  if (migrated) {
    try {
      saveSettings(settings);
    } catch {
      /* storage full or blocked: migrated again on the next load */
    }
  }
  return settings;
}

export function saveSettings(settings: Settings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function clearSettings(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function hasCredentials(s: Settings): boolean {
  return Boolean(s.serverUrl && s.apiKey);
}

/** The self-hosted server shown in Settings → Advanced ('' = Qodoonect's own). */
export function customServerOf(s: Settings): string {
  return s.serverUrl === SAME_ORIGIN ? '' : s.serverUrl;
}

type BuildEnv = Record<string, string | boolean | undefined>;

function buildEnv(): BuildEnv {
  try {
    return ((import.meta as any).env || {}) as BuildEnv;
  } catch {
    return {};
  }
}

/**
 * Does this build offer "Advanced — self-hosted HIVE server" at all? The hosted site
 * may connect only to itself (vercel.json CSP: connect-src 'self'), so another server
 * could never be reached there and the field would only mislead. Production builds
 * leave it out unless VITE_SELF_HOSTED_SERVER=on (a build hosted with its own CSP and
 * proxy); dev builds offer it. The sign-in form and Settings also show it when a
 * server is already saved (`saved`), so it can be cleared.
 */
export function selfHostedServerOffered(saved: Settings | null = null, env: BuildEnv = buildEnv()): boolean {
  if (saved && customServerOf(saved)) return true;
  const v = typeof env.VITE_SELF_HOSTED_SERVER === 'string' ? env.VITE_SELF_HOSTED_SERVER.trim().toLowerCase() : '';
  if (v === 'on') return true;
  if (v === 'off') return false;
  return env.DEV === true;
}

/**
 * Settings → Advanced input → serverUrl to save: empty (or the old default address)
 * = SAME_ORIGIN, otherwise a checked self-hosted address (checkServerUrl).
 */
export function serverUrlFromInput(raw: string): { ok: true; serverUrl: string } | { ok: false; message: string } {
  const r = checkServerUrl(raw);
  if (!r.ok) return r;
  if (!r.base || isLegacyDefaultServer(r.base)) return { ok: true, serverUrl: SAME_ORIGIN };
  return { ok: true, serverUrl: r.base };
}
